// In-memory implementation of LeaveDataService. State is a module-level
// singleton so create/approve/cancel persist while you navigate between screens
// in a session (a page refresh resets to seed).

import {
  CalendarEvent,
  CurrentUser,
  Employee,
  Holiday,
  LeaveBalance,
  LeaveRequest,
  LeaveType,
} from "../models/leaveModels";
import { toISODate } from "../utils/dateUtils";
import { eventInWindow, requestToEvents } from "../utils/calendarEvents";
import { Decision, LeaveDataService } from "./leaveService";
import {
  MOCK_CURRENT_USER,
  seedBalances,
  seedEmployees,
  seedHolidays,
  seedLeaveTypes,
  seedRequests,
} from "./mockData";

const wait = (ms = 180) => new Promise((r) => setTimeout(r, ms));
const clone = <T>(v: T): T =>
  typeof structuredClone === "function"
    ? structuredClone(v)
    : JSON.parse(JSON.stringify(v));

export class MockLeaveService implements LeaveDataService {
  private static _instance: MockLeaveService | null = null;

  static instance(): MockLeaveService {
    if (!this._instance) this._instance = new MockLeaveService();
    return this._instance;
  }

  private employees: Employee[] = clone(seedEmployees);
  private leaveTypes: LeaveType[] = clone(seedLeaveTypes);
  private balances: LeaveBalance[] = clone(seedBalances);
  private holidays: Holiday[] = clone(seedHolidays);
  private requests: LeaveRequest[] = clone(seedRequests);
  private counter = 2000;
  /** The employee the demo is currently "acting as" (dev user switcher). */
  private actingEmail = MOCK_CURRENT_USER.email;

  async getCurrentUser(): Promise<CurrentUser> {
    await wait();
    const emp = this.employees.find(
      (e) => e.email.toLowerCase() === this.actingEmail.toLowerCase(),
    );
    return emp ? { name: emp.name, email: emp.email } : clone(MOCK_CURRENT_USER);
  }

  /** Mock-only: switch which seed employee the app is signed in as. */
  setActingUser(email: string): void {
    this.actingEmail = email;
  }

  /** Mock-only: everyone you can view the demo as. */
  async getSwitchableUsers(): Promise<CurrentUser[]> {
    return this.employees.map((e) => ({ name: e.name, email: e.email }));
  }

  async getEmployeeByEmail(email: string): Promise<Employee | null> {
    await wait();
    const e = this.employees.find(
      (x) => x.email.toLowerCase() === email.toLowerCase(),
    );
    return e ? clone(e) : null;
  }

  async getLeaveTypes(): Promise<LeaveType[]> {
    await wait();
    return clone(this.leaveTypes);
  }

  async getHolidays(year: number): Promise<Holiday[]> {
    await wait();
    return clone(this.holidays.filter((h) => (h.year ?? year) === year));
  }

  async getMyLeaveRequests(userEmail: string): Promise<LeaveRequest[]> {
    await wait();
    return clone(
      this.requests
        .filter((r) => r.employeeEmail.toLowerCase() === userEmail.toLowerCase())
        .sort((a, b) => (b.filedDate ?? "").localeCompare(a.filedDate ?? "")),
    );
  }

  async getPendingForManager(managerEmail: string): Promise<LeaveRequest[]> {
    await wait();
    return clone(
      this.requests.filter(
        (r) =>
          r.managerEmail.toLowerCase() === managerEmail.toLowerCase() &&
          r.status === "Pending",
      ),
    );
  }

  async getMyBalances(userEmail: string, year: number): Promise<LeaveBalance[]> {
    await wait();
    return clone(
      this.balances.filter(
        (b) =>
          b.employeeEmail.toLowerCase() === userEmail.toLowerCase() &&
          b.year === year,
      ),
    );
  }

  async createLeaveRequest(request: LeaveRequest, attachment?: File): Promise<LeaveRequest> {
    await wait();
    const id = String(++this.counter);
    const now = new Date().toISOString();
    const emp = this.employees.find(
      (e) => e.email.toLowerCase() === request.employeeEmail.toLowerCase(),
    );
    const stamp = toISODate(new Date()).replace(/-/g, "");
    const created: LeaveRequest = {
      ...request,
      id,
      title: `LR-${stamp}-${id}`,
      employee: emp?.name ?? request.employee,
      managerEmail: emp?.managerEmail ?? request.managerEmail,
      status: "Pending",
      filedDate: now,
      pendingSince: now,
      // Demo only: a browser-local link so the manager can open the file this session.
      attachmentUrl: attachment ? URL.createObjectURL(attachment) : request.attachmentUrl,
      attachmentName: attachment?.name ?? request.attachmentName,
    };
    this.requests.push(created);
    return clone(created);
  }

  async decideLeaveRequest(
    requestId: string,
    decision: Decision,
    approverEmail: string,
    notes: string,
  ): Promise<void> {
    await wait();
    const req = this.requests.find((r) => r.id === requestId);
    if (!req) throw new Error(`Request ${requestId} not found`);
    req.status = decision;
    req.approver = approverEmail;
    req.decisionDate = new Date().toISOString();
    req.decisionNotes = notes;

    if (decision === "Approved") this.applyBalanceDelta(req, +1);
  }

  async cancelLeaveRequest(requestId: string): Promise<void> {
    await wait();
    const req = this.requests.find((r) => r.id === requestId);
    if (!req) throw new Error(`Request ${requestId} not found`);
    const wasApproved =
      req.status === "Approved" || req.status === "Auto-Approved";
    req.status = "Cancelled";
    req.decisionDate = new Date().toISOString();

    if (wasApproved) this.applyBalanceDelta(req, -1); // refund
  }

  async getTeamCalendarEvents(
    startISO: string,
    endISO: string,
  ): Promise<CalendarEvent[]> {
    await wait();
    // Same as graph mode's default: derived from approved requests.
    return this.requests
      .filter((r) => r.status === "Approved" || r.status === "Auto-Approved")
      .flatMap(requestToEvents)
      .filter((e) => eventInWindow(e, startISO, endISO));
  }

  // --- helpers -------------------------------------------------------------

  private applyBalanceDelta(req: LeaveRequest, sign: 1 | -1): void {
    const type = this.leaveTypes.find((t) => t.code === req.leaveTypeCode);
    if (!type || !type.deductsBalance) return;
    const year = Number(req.startDate.slice(0, 4));
    const bal = this.balances.find(
      (b) =>
        b.employeeEmail.toLowerCase() === req.employeeEmail.toLowerCase() &&
        b.leaveTypeCode === req.leaveTypeCode &&
        b.year === year,
    );
    if (!bal) return;
    // LWOP days never touch the balance.
    const paidDays = Math.max(0, req.workingDays - req.lwopDays);
    bal.used = Math.max(0, bal.used + sign * paidDays);
    bal.remaining = bal.entitlement - bal.used;
  }
}
